import type { TFunction } from 'i18next';

import type { MetricAxisSpec, MetricSeriesSpec } from '@/components/charts/generic';
import type { FeedingOverlaySources, FeedingSeriesPoint } from '@/domain/feedingDashboard';

import { FEEDING_COLORS } from './feedingChartColors';

export type EnvOverlay = 'temperature' | 'oxygen' | 'oxygenSaturation' | 'salinity';

/** Every overlay, in legend order. */
export const ENV_OVERLAYS: readonly EnvOverlay[] = [
    'temperature',
    'oxygen',
    'oxygenSaturation',
    'salinity',
] as const;

// Four coloured, unit-suffixed scales beside the weight axis; more ticks than this crowd
// out the plot.
const OVERLAY_MAX_TICKS = 5;

const OVERLAY_CONFIG: Record<
    EnvOverlay,
    {
        color: string;
        unit: string;
        selector: (point: FeedingSeriesPoint) => number | null;
        source: keyof FeedingOverlaySources;
        /**
         * The smallest range the axis shows, in the overlay's unit. Judgement values: about
         * what the quantity moves in a pen over a few weeks, so a handful of near-equal
         * readings draws as the flat line it is rather than a squiggle filling the plot.
         */
        minSpan: number;
    }
> = {
    temperature: {
        color: FEEDING_COLORS.temperature,
        unit: '°C',
        selector: (point) => point.temperatureC,
        source: 'temperatureC',
        minSpan: 2,
    },
    oxygen: {
        color: FEEDING_COLORS.oxygen,
        unit: 'mg/L',
        selector: (point) => point.oxygenMgL,
        source: 'oxygenMgL',
        minSpan: 2,
    },
    // Saturation is plotted separately from concentration rather than converted into
    // it: the conversion depends on temperature and salinity, so a single "oxygen"
    // series would silently mix two different measurements. Values above 100 are
    // legitimate (supersaturation), which is also why it cannot share the mg/L axis.
    oxygenSaturation: {
        color: FEEDING_COLORS.oxygenSaturation,
        unit: '%',
        selector: (point) => point.oxygenSaturationPct,
        source: 'oxygenSaturationPct',
        minSpan: 10,
    },
    salinity: {
        color: FEEDING_COLORS.salinity,
        unit: '‰',
        selector: (point) => point.salinityPpt,
        source: 'salinityPpt',
        minSpan: 2,
    },
};

export interface FeedWeightOverlays {
    series: MetricSeriesSpec[];
    axes: Record<string, MetricAxisSpec>;
    /** Overlays with no reading in the period, so not offered; in legend order. */
    missing: EnvOverlay[];
}

/**
 * The legend label, naming the planning system wherever it supplied the values, or may
 * have (a previous backend did not say).
 *
 * A pen's line is filled per day, the Bioscope first and the planning system on the days
 * it missed, so the same curve can mean the water in the cage or one site reading repeated
 * for every pen. Only the label can tell the two apart; the line looks identical.
 */
function overlayLabel(
    name: string,
    source: FeedingOverlaySources[keyof FeedingOverlaySources],
    systemName: string,
    t: TFunction
): string {
    if (source === 'planningSystem') {
        return t('feedingModule.charts.overlayFromSystem', { overlay: name, system: systemName });
    }
    if (source === 'mixed') {
        return t('feedingModule.charts.overlayPartlyFromSystem', {
            overlay: name,
            system: systemName,
        });
    }
    if (source === 'unknown') {
        return t('feedingModule.charts.overlayPossiblyFromSystem', {
            overlay: name,
            system: systemName,
        });
    }
    return name;
}

/**
 * Soft bounds that give a narrow range at least `minSpan`, centred on its midpoint. Soft,
 * so readings beyond them still widen the axis, and the axis `grace` still applies.
 */
function minimumSpan(values: number[], minSpan: number): Partial<MetricAxisSpec> {
    const low = Math.min(...values);
    const high = Math.max(...values);
    if (high - low >= minSpan) return {};
    const middle = (low + high) / 2;
    return { suggestedMin: middle - minSpan / 2, suggestedMax: middle + minSpan / 2 };
}

export function buildFeedWeightOverlays(
    series: FeedingSeriesPoint[],
    t: TFunction,
    sources: FeedingOverlaySources,
    systemName: string
): FeedWeightOverlays {
    const overlays: FeedWeightOverlays = { series: [], axes: {}, missing: [] };
    for (const overlay of ENV_OVERLAYS) {
        const { color, unit, selector, source, minSpan } = OVERLAY_CONFIG[overlay];
        const readings = series.flatMap((point) => {
            const value = selector(point);
            return value == null ? [] : [value];
        });
        // An overlay with no value anywhere in the period is dropped rather than offered.
        // The rule for the core series is the opposite (a wired-but-silent series stays
        // so its gap is visible), but that reasoning does not carry here: an overlay is
        // hidden by default, so its absence is never plotted. All that would remain is a
        // legend entry that reveals nothing when clicked. The caption names it instead.
        if (readings.length === 0) {
            overlays.missing.push(overlay);
            continue;
        }
        overlays.series.push({
            key: overlay,
            label: overlayLabel(
                t(`feedingModule.charts.${overlay}`),
                sources[source],
                systemName,
                t
            ),
            color,
            type: 'line',
            axis: overlay,
            hiddenByDefault: true,
            data: series.map((point) => ({ day: point.bucket, value: selector(point) })),
        });
        overlays.axes[overlay] = {
            // No title: the ticks carry the series colour and unit, and four titled
            // scales took about a third of the plot width.
            label: '',
            unit,
            color,
            maxTicksLimit: OVERLAY_MAX_TICKS,
            // Fitted to the data with a little headroom, NOT anchored at zero. Water
            // temperature moves between about 8 and 12 over a season; on a 0-based axis
            // that is a straight line, and the whole point of the overlay is the shape.
            grace: '20%',
            ...minimumSpan(readings, minSpan),
        };
    }
    return overlays;
}
